import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button } from "@mui/material";
import { trackCopyContent } from './analytics.js';

/**
 * Mapping of problematic Unicode characters to ASCII equivalents.
 * These characters cause encoding issues (UTF-8 interpreted as Latin-1) when copied
 * to Word/Google Docs, appearing as mojibake like "â€"" instead of "—".
 * 
 * Solution: Replace with ASCII equivalents that work universally.
 */
const UNICODE_TO_ASCII_MAP = {
    '\u00A0': ' ',      // Non-breaking space → regular space
    '\u2013': '-',      // En-dash → hyphen
    '\u2014': '--',     // Em-dash → double hyphen
    '\u2018': "'",      // Left single quote → straight quote
    '\u2019': "'",      // Right single quote/apostrophe → straight quote
    '\u201C': '"',      // Left double quote → straight quote
    '\u201D': '"',      // Right double quote → straight quote
    '\u2026': '...',    // Horizontal ellipsis → three dots
    '\u00AB': '<<',     // Left-pointing double angle quotation mark
    '\u00BB': '>>',     // Right-pointing double angle quotation mark
    '\u201A': ',',      // Single low-9 quotation mark
    '\u201B': "'",      // Single high-reversed-9 quotation mark
    '\u201E': '"',      // Double low-9 quotation mark
    '\u201F': '"',      // Double high-reversed-9 quotation mark
    '\u2032': "'",      // Prime (feet, minutes)
    '\u2033': '"',      // Double prime (inches, seconds)
    '\u2012': '-',      // Figure dash
    '\u2015': '--',     // Horizontal bar
    '\u2010': '-',      // Hyphen
    '\u2011': '-',      // Non-breaking hyphen
    '\u2212': '-',      // Minus sign
    '\u00AD': '',       // Soft hyphen (invisible)
    '\u200B': '',       // Zero-width space
    '\u200C': '',       // Zero-width non-joiner
    '\u200D': '',       // Zero-width joiner
    '\uFEFF': '',       // Byte order mark
};

/**
 * Replaces problematic HTML entities with regular characters to prevent
 * encoding issues when copying to clipboard and pasting into Word/Google Docs.
 * 
 * @param {string} htmlString - The HTML string to process
 * @returns {string} HTML string with problematic entities replaced
 */
function replaceHtmlEntities(htmlString) {
    if (!htmlString) return htmlString;

    // Replace HTML entities that cause encoding issues
    // These entities get converted to Unicode characters that may be misinterpreted
    return htmlString
        .replace(/&nbsp;/g, ' ')           // Non-breaking space → regular space
        .replace(/&#160;/g, ' ')            // Non-breaking space (numeric) → regular space
        .replace(/&#xA0;/g, ' ')            // Non-breaking space (hex) → regular space
        .replace(/&ndash;/g, '-')           // En-dash → hyphen
        .replace(/&#8211;/g, '-')           // En-dash (numeric) → hyphen
        .replace(/&mdash;/g, '--')          // Em-dash → double hyphen
        .replace(/&#8212;/g, '--')          // Em-dash (numeric) → double hyphen
        .replace(/&lsquo;/g, "'")           // Left single quote → straight quote
        .replace(/&#8216;/g, "'")           // Left single quote (numeric) → straight quote
        .replace(/&rsquo;/g, "'")           // Right single quote → straight quote
        .replace(/&#8217;/g, "'")           // Right single quote (numeric) → straight quote
        .replace(/&ldquo;/g, '"')           // Left double quote → straight quote
        .replace(/&#8220;/g, '"')           // Left double quote (numeric) → straight quote
        .replace(/&rdquo;/g, '"')           // Right double quote → straight quote
        .replace(/&#8221;/g, '"')           // Right double quote (numeric) → straight quote
        .replace(/&hellip;/g, '...')        // Horizontal ellipsis → three dots
        .replace(/&#8230;/g, '...')         // Horizontal ellipsis (numeric) → three dots
        .replace(/&shy;/g, '')              // Soft hyphen (invisible)
        .replace(/&#173;/g, '');            // Soft hyphen (numeric) (invisible)
}

/**
 * Normalizes text content in a DOM tree by replacing problematic Unicode characters
 * with ASCII equivalents in all text nodes.
 * 
 * @param {Node} node - The DOM node to process (will process all descendants)
 */
function normalizeTextNodes(node) {
    if (!node) return;

    // Process text nodes
    if (node.nodeType === Node.TEXT_NODE) {
        let text = node.textContent;
        // Replace known problematic characters with ASCII equivalents
        for (const [unicode, ascii] of Object.entries(UNICODE_TO_ASCII_MAP)) {
            text = text.replace(new RegExp(unicode, 'g'), ascii);
        }
        node.textContent = text;
    } else {
        // Recursively process child nodes
        const children = Array.from(node.childNodes);
        children.forEach(child => normalizeTextNodes(child));
    }
}

/**
 * Replaces problematic Unicode characters with ASCII equivalents to prevent
 * encoding issues when copying to clipboard and pasting into Word/Google Docs.
 * 
 * @param {string} text - The text to process
 * @returns {string} Text with problematic Unicode replaced with ASCII equivalents
 */
export function normalizeUnicodeToAscii(text) {
    if (!text) return text;

    let processed = text;

    // Replace known problematic characters with ASCII equivalents
    for (const [unicode, ascii] of Object.entries(UNICODE_TO_ASCII_MAP)) {
        processed = processed.replace(new RegExp(unicode, 'g'), ascii);
    }

    return processed;
}

/**
 * Removes unwanted elements and classes from HTML content before copying.
 * Removes elements with classes "citation", "needs-review", "caveat".
 * Removes classes "warning-needs-review" and "warning-caveat" from elements.
 * Also normalizes text content to prevent encoding issues.
 * 
 * @param {string} htmlString - The HTML string to process
 * @returns {string} Processed HTML string with unwanted elements and classes removed, and text normalized
 */
function cleanHtmlForCopy(htmlString) {
    if (!htmlString) return htmlString;

    // First, replace HTML entities that cause encoding issues
    const htmlWithoutEntities = replaceHtmlEntities(htmlString);

    // Create a temporary container to parse and manipulate the HTML
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlWithoutEntities;

    // Remove elements with classes "citation", "needs-review", "caveat"
    const classesToRemove = ['citation', 'needs-review', 'caveat'];
    classesToRemove.forEach(className => {
        const elementsToRemove = tempContainer.querySelectorAll(`.${className}`);
        elementsToRemove.forEach(element => {
            element.remove();
        });
    });

    // Remove classes "warning-needs-review" and "warning-caveat" from all elements
    const warningClassesToRemove = ['warning-needs-review', 'warning-caveat'];
    const allElements = tempContainer.querySelectorAll('*');
    allElements.forEach(element => {
        warningClassesToRemove.forEach(className => {
            if (element.classList.contains(className)) {
                element.classList.remove(className);
            }
        });
    });

    // Normalize all text nodes to replace problematic Unicode characters
    normalizeTextNodes(tempContainer);

    // Return the cleaned HTML
    return tempContainer.innerHTML;
}

/**
 * Processes HTML string to add borders and formatting to tables
 * @param {string} htmlString - The HTML string to process
 * @returns {string} HTML string with tables processed for borders and formatting
 */
function processTablesInHtml(htmlString) {
    if (!htmlString) return htmlString;

    // Create a temporary container to parse and manipulate the HTML
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlString;

    // Find all tables in the HTML
    const tables = tempContainer.querySelectorAll('table');

    tables.forEach(table => {
        // Set table width to fit Word document margins (standard 8.5" page with 1" margins = 6.5" content width)
        table.style.width = '6.5in';
        table.style.maxWidth = '6.5in';
        table.style.tableLayout = 'auto';
        table.style.borderCollapse = 'collapse';
        table.style.margin = '0';

        // Process all cells to ensure proper formatting
        const cells = table.querySelectorAll('td, th');
        cells.forEach(cell => {
            // Remove min-width constraints that cause Word to expand the table
            cell.style.minWidth = '';
            cell.style.maxWidth = ''; // Let Word handle column widths proportionally
            // Set word-wrap to handle long content
            cell.style.wordWrap = 'break-word';
            cell.style.overflowWrap = 'break-word';
            // Ensure padding is reasonable
            if (!cell.style.padding || cell.style.padding === '0px') {
                cell.style.padding = '4px 8px';
            }
            // Ensure borders are visible - check both inline style and computed style
            const hasBorder = cell.style.border && cell.style.border !== 'none' && cell.style.border !== '';
            const hasBorderAttribute = cell.hasAttribute('border');

            // If no border is set, add one (using the same color as the editor: --border-color = #e1e5e9)
            if (!hasBorder && !hasBorderAttribute) {
                cell.style.border = '1px solid #e1e5e9';
            }
        });

        // Process table rows
        const rows = table.querySelectorAll('tr');
        rows.forEach(row => {
            row.style.margin = '0';
        });
    });

    return tempContainer.innerHTML;
}

/**
 * Converts an HTML table element to Word-compatible HTML with proper width constraints
 * @param {HTMLElement} tableElement - The table DOM element
 * @returns {HTMLElement} Cloned table element with Word-compatible styles
 */
function convertTableToWordCompatible(tableElement) {
    if (!tableElement) return null;

    // Deep clone the table to avoid modifying the original
    const clonedTable = tableElement.cloneNode(true);

    // Set table width to fit Word document margins (standard 8.5" page with 1" margins = 6.5" content width)
    clonedTable.style.width = '6.5in';
    clonedTable.style.maxWidth = '6.5in';
    clonedTable.style.tableLayout = 'auto';
    clonedTable.style.borderCollapse = 'collapse';
    clonedTable.style.margin = '0';

    // Process all cells to ensure proper formatting
    const cells = clonedTable.querySelectorAll('td, th');
    cells.forEach(cell => {
        // Remove min-width constraints that cause Word to expand the table
        cell.style.minWidth = '';
        cell.style.maxWidth = ''; // Let Word handle column widths proportionally
        // Set word-wrap to handle long content
        cell.style.wordWrap = 'break-word';
        cell.style.overflowWrap = 'break-word';
        // Ensure padding is reasonable
        if (!cell.style.padding || cell.style.padding === '0px') {
            cell.style.padding = '4px 8px';
        }
        // Ensure borders are visible (using the same color as the editor: --border-color = #e1e5e9)
        if (!cell.style.border && !cell.hasAttribute('border')) {
            cell.style.border = '1px solid #e1e5e9';
        }
    });

    // Process table rows
    const rows = clonedTable.querySelectorAll('tr');
    rows.forEach(row => {
        row.style.margin = '0';
    });

    return clonedTable;
}

/**
 * Displays a Copy to Clipboard button which when clicked, will copy the contents
 * of the given DOM node to clipboard and display a message on snackBar.
 * @param nodeRef ref to the DOM node whose contents should be copied to clipboard.
 * @param getContent optional function that returns HTML string to copy (takes precedence over nodeRef)
 * @param setSnackBarMessage method to set a message to appear in the snack bar.
 * @param buttonText text to display in the button.
 * @param wordCompatible if true, formats the content for better Word document compatibility
 * @param contentType analytics label for copy_content event (no case content sent)
 */
export default function CopyToClipboardButton({
    nodeRef,
    getContent,
    setSnackBarMessage,
    buttonText = 'Copy to clipboard',
    wordCompatible = true,
    contentType,
}) {
    function reportCopySuccess() {
        setSnackBarMessage('Copied to Clipboard');
        if (contentType) {
            trackCopyContent(contentType);
        }
    }

    function reportCopySuccessAsText() {
        setSnackBarMessage('Copied to Clipboard (as text)');
        if (contentType) {
            trackCopyContent(contentType);
        }
    }

    return (
        <Button size="small" variant="text" data-testid="copy-button" onClick={async () => {
            // If getContent function is provided, use it (preferred for TipTap and other editors)
            if (getContent) {
                try {
                    const htmlString = getContent();
                    if (!htmlString) {
                        setSnackBarMessage('Error: No content to copy.');
                        return;
                    }

                    // Process tables to add borders and formatting (if wordCompatible is enabled)
                    let processedHtml = htmlString;
                    if (wordCompatible) {
                        processedHtml = processTablesInHtml(htmlString);
                    }

                    // Clean HTML: remove unwanted elements and classes, normalize text
                    const cleanedHtml = cleanHtmlForCopy(processedHtml);

                    // Try using the modern Clipboard API with HTML format (preferred method)
                    if (navigator.clipboard && navigator.clipboard.write) {
                        try {
                            // Create a Blob with the HTML content
                            const htmlBlob = new Blob([cleanedHtml], { type: 'text/html' });

                            // Get plain text version for fallback
                            const tempDiv = document.createElement('div');
                            tempDiv.innerHTML = cleanedHtml;
                            const plainText = normalizeUnicodeToAscii(tempDiv.textContent || tempDiv.innerText || '');

                            // Create clipboard items with both HTML and plain text
                            const clipboardItem = new ClipboardItem({
                                'text/html': htmlBlob,
                                'text/plain': new Blob([plainText], { type: 'text/plain' })
                            });

                            await navigator.clipboard.write([clipboardItem]);
                            reportCopySuccess();
                            return;
                        } catch (clipboardError) {
                            // Fall through to execCommand method if Clipboard API fails
                            console.warn('Clipboard API failed, falling back to execCommand:', clipboardError);
                        }
                    }

                    // Fallback to execCommand method which works reliably with HTML content
                    // This method preserves HTML formatting and works well with Word/Google Docs
                    const tempContainer = document.createElement('div');
                    tempContainer.style.position = 'absolute';
                    tempContainer.style.left = '-9999px';
                    tempContainer.style.top = '0';
                    tempContainer.innerHTML = cleanedHtml;
                    document.body.appendChild(tempContainer);

                    try {
                        const range = document.createRange();
                        range.selectNodeContents(tempContainer);
                        const selection = window.getSelection();
                        selection.removeAllRanges();
                        selection.addRange(range);

                        const success = document.execCommand('copy');
                        selection.removeAllRanges();
                        document.body.removeChild(tempContainer);

                        if (success) {
                            reportCopySuccess();
                        } else {
                            // Final fallback to Clipboard API text-only if execCommand fails
                            if (navigator.clipboard && navigator.clipboard.writeText) {
                                const tempDiv = document.createElement('div');
                                tempDiv.innerHTML = cleanedHtml;
                                const plainText = normalizeUnicodeToAscii(tempDiv.textContent || tempDiv.innerText || '');
                                await navigator.clipboard.writeText(plainText);
                                reportCopySuccessAsText();
                            } else {
                                setSnackBarMessage('Error: Copy to clipboard not supported.');
                            }
                        }
                    } catch (error) {
                        document.body.removeChild(tempContainer);
                        console.error('Error copying content:', error);
                        setSnackBarMessage('Error: Failed to copy content.');
                    }
                    return;
                } catch (error) {
                    console.error('Error copying content:', error);
                    setSnackBarMessage('Error: Failed to copy content.');
                    return;
                }
            }

            // Fallback to DOM-based copying if no getContent function
            if (!nodeRef?.current) {
                setSnackBarMessage('Error: No content to copy.');
                return;
            }

            // If wordCompatible is enabled, format for Word before copying
            if (wordCompatible) {
                try {
                    const node = nodeRef.current;

                    // Check if it's a table element
                    let elementToCopy;
                    if (node.tagName === 'TABLE' || node.querySelector('table')) {
                        const table = node.tagName === 'TABLE' ? node : node.querySelector('table');
                        elementToCopy = convertTableToWordCompatible(table);
                        if (!elementToCopy) {
                            // Fallback to cloned original if conversion failed
                            elementToCopy = node.cloneNode(true);
                        }
                    } else {
                        // For non-table content, use as-is
                        elementToCopy = node.cloneNode(true);
                    }

                    // Get the HTML string from the cloned element
                    let htmlString = elementToCopy.outerHTML || elementToCopy.innerHTML;

                    // Clean HTML: remove unwanted elements and classes, normalize text
                    const cleanedHtml = cleanHtmlForCopy(htmlString);

                    // Try using the modern Clipboard API with HTML format (preferred method)
                    if (navigator.clipboard && navigator.clipboard.write) {
                        try {
                            // Create a Blob with the HTML content
                            const htmlBlob = new Blob([cleanedHtml], { type: 'text/html' });

                            // Get plain text version for fallback
                            const tempDiv = document.createElement('div');
                            tempDiv.innerHTML = cleanedHtml;
                            const plainText = normalizeUnicodeToAscii(tempDiv.textContent || tempDiv.innerText || '');

                            // Create clipboard items with both HTML and plain text
                            const clipboardItem = new ClipboardItem({
                                'text/html': htmlBlob,
                                'text/plain': new Blob([plainText], { type: 'text/plain' })
                            });

                            await navigator.clipboard.write([clipboardItem]);
                            reportCopySuccess();
                            return;
                        } catch (clipboardError) {
                            // Fall through to execCommand method if Clipboard API fails
                            console.warn('Clipboard API failed, falling back to execCommand:', clipboardError);
                        }
                    }

                    // Fallback to execCommand method
                    const tempContainer = document.createElement('div');
                    tempContainer.style.position = 'absolute';
                    tempContainer.style.left = '-9999px';
                    tempContainer.style.top = '0';
                    tempContainer.innerHTML = cleanedHtml;
                    document.body.appendChild(tempContainer);

                    // Select and copy using execCommand
                    const range = document.createRange();
                    range.selectNodeContents(tempContainer);
                    const selection = window.getSelection();
                    selection.removeAllRanges();
                    selection.addRange(range);
                    document.execCommand('copy');
                    selection.removeAllRanges();

                    // Clean up
                    document.body.removeChild(tempContainer);

                    reportCopySuccess();
                    return;
                } catch (error) {
                    console.warn('Word-compatible copy failed, falling back to standard copy:', error);
                    // Fall through to standard copy method
                }
            }

            // Standard copy method (when wordCompatible is false or wordCompatible path failed)
            let range, selection;
            if (document.createRange && window.getSelection) {
                range = document.createRange();
                selection = window.getSelection();
                selection.removeAllRanges();

                try {
                    range.selectNodeContents(nodeRef.current);
                    selection.addRange(range);
                } catch (e) {
                    range.selectNode(nodeRef.current);
                    selection.addRange(range);
                }

                document.execCommand('copy');
                selection.removeAllRanges();
                reportCopySuccess();
            } else {
                setSnackBarMessage('Error: Copy to clipboard not supported by browser.');
            }
        }}><ContentCopyIcon></ContentCopyIcon>{buttonText}</Button>);
}